import { getMediaUrl } from "../../../../services/helpers/get-media-url";
import { apiFetch } from "../../../../services/api-fetch";

/**
 * @typedef {Object} CommentData
 * @description Данные комментария для редактирования
 * 
 * @property {string} comment Текст комментария
 * @property {string[]} images Загруженные изображения
 */

/**
 * Форма комментирования
 * @class CommentForm
 * @classdesc Форма для ответа на комментарии и редактирования комментариев
 * 
 * @property {HTMLElement} form
 * @property {string} state Стейт формы: edit/answer, определяет экшн
 * @property {CommentEntity} entity Комментарий, с которым форма взаимодействует (родительский или редактируемый)
 */
class CommentForm {
  /**
   * @constructor
   * @param {HTMLElement} form 
   */
  constructor(form, config) {
    this._form = form;

    this._replaceFragment = new RegExp(this._form.dataset.replace, "g");

    if (config.onCommentCreate && typeof config.onCommentCreate === "function") {
      this.onCommentCreate = config.onCommentCreate.bind(this);
    }

    this.nodes = {
      comment: this._form.querySelector(`[name="commentContent"]`),
      previewContainer: this._form.querySelector(`[data-previews]`),
      images: [],
      from: this._form.querySelector(`[name="from_company"]`) || {},
      previewTmp: this._form.querySelector(`[data-preview-tmp]`),
      submit: this._form.querySelector(`[type="submit"]`),
    };

    this.nodes.comment.addEventListener("keydown", (e) => {
      if (!window.checkAuth()) {
        e.preventDefault();
      }
    })

    this._actions = this._form.dataset;

    this._form.onsubmit = (e) => {
      e.preventDefault();
      if (!window.checkAuth()) {
        e.preventDefault();
        return false;
      }
      this._onSubmit();
    }
  }

  get urlsCount() {
    return this._form.querySelectorAll(`[name="commentImgUrls[]"]`).length;
  }

  get form() {
    return this._form;
  }

  get state() {
    return this._state;
  }

  set state(state) {
    this._state = state;
    let action = this._form.dataset[`${state}State`];
    if (this.entity) {
      action = action.replace(this._replaceFragment, this.entity.id);
    }
    this._form.action = action;
  }

  get entity() {
    return this._entity;
  }

  set entity(entity) {
    this._entity = entity;
  }

  get text() {
    return this.nodes.comment.value;
  }

  set text(text) {
    this.nodes.comment.value = text;
  }

  set from(from) {
    this.nodes.from.checked = !!from;
  }

  focus() {
    this.nodes.comment.focus();
  }

  /**
   * Устанавливает данные для редактирования комментария
   * @param {CommentData} data 
   */
  setData(data) {
    this.text = data.comment || "";
    this.from = data.from;

    data.images.forEach((url) => {
      this._createImageField(url);
    });
  }

  _createImageField(url) {
    let input = document.createElement("input");
    input.type = "hidden";
    input.name = 'commentImgUrls[]';
    input.value = url;

    let preview = this.nodes.previewTmp.cloneNode(true);
    preview.querySelector("img").src = getMediaUrl(url);
    preview.appendChild(input);
    
    this.nodes.previewContainer.appendChild(preview);

    let image = {
      input: input,
      preview: preview
    };
    this.nodes.images.push(image);

    preview.querySelector("[data-close]")
      .addEventListener("click", () => {
        this._deleteImage(image);
      });
  }

  _deleteImage(image) {
    image.input.remove();
    image.preview.remove();

    let index = this.nodes.images.indexOf(image);
    if (index !== -1) {
      this.nodes.images.splice(index, 1);
    }
  }

  clear() {
    this.text = "";

    this.nodes.images.forEach(image => {
      image.preview.remove();
      image.input.remove();
    });

    this.nodes.images.length = 0;

    this.nodes.previewContainer.innerHTML = '';
  }

  close() {
    this._form.remove();
  }

  _onSubmit() {
    this.nodes.submit.setAttribute('data-loading', '');
    this.nodes.submit.disabled = true;

    let data = new FormData(this._form);

    apiFetch(this._form.action, {
      method: "POST",
      body: data
    })
    .then(result => {
      this.nodes.submit.removeAttribute('data-loading');
      this.nodes.submit.disabled = false;

      if (this.onCommentCreate) {
        this.onCommentCreate(result);
      }
    })
    .catch(error => {
      this.nodes.submit.removeAttribute('data-loading');
      this.nodes.submit.disabled = false;

      console.error(error);
    });
  }
}

export default CommentForm;